import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { getCoverageSettings, saveCoverageSettings } from "services/coverageSettings";
const { DEFAULT_COVERAGE_SETTINGS, validateCoverageSettings } = require("shared/coverageSettings");
const display = (value) => Number(value).toLocaleString("pt-BR");
const fields = (value) => ({
  minKPercent: String(value.minKPercent),
  colorCoveragePercent: String(value.colorCoveragePercent),
});

export default function CoverageSettingsAdmin() {
  const [saved, setSaved] = useState(null);
  const [form, setForm] = useState(fields(DEFAULT_COVERAGE_SETTINGS));
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    getCoverageSettings()
      .then((value) => {
        if (active) {
          setSaved(value);
          setForm(fields(value));
        }
      })
      .catch((failure) => {
        if (active) setError(failure.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [revision]);
  let parsed;
  try {
    if (Object.values(form).some((value) => !value.trim())) throw new Error();
    parsed = validateCoverageSettings(
      Object.fromEntries(
        Object.entries(form).map(([key, value]) => [key, Number(value.replace(",", "."))])
      )
    );
  } catch (_) {
    /* Incomplete edits are not submitted. */
  }
  const invalidField = (key) => {
    try {
      const value = form[key].trim() ? Number(form[key].replace(",", ".")) : NaN;
      validateCoverageSettings({ ...DEFAULT_COVERAGE_SETTINGS, [key]: value });
      return false;
    } catch (_) {
      return true;
    }
  };
  const dirty =
    saved &&
    parsed &&
    (parsed.minKPercent !== saved.minKPercent ||
      parsed.colorCoveragePercent !== saved.colorCoveragePercent);
  const save = async (event) => {
    event.preventDefault();
    if (!parsed || !saved || busy) return;
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const value = await saveCoverageSettings(parsed);
      setSaved(value);
      setForm(fields(value));
      setSuccess("Regra salva. As próximas análises usarão os novos limites.");
    } catch (failure) {
      setError(failure.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Box
      className="admin-category-panel"
      component="section"
      aria-label="Configuração da cobertura do miolo"
    >
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, mb: 1 }}>
        <Typography variant="h5">Cobertura do miolo</Typography>
        <Chip size="small" label="Regra global" />
        {dirty && <Chip size="small" color="warning" label="Alterações não salvas" />}
      </Box>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Defina quais pixels entram na contagem e quando a cobertura classifica o livro como
        colorido.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      {loading ? (
        <Box sx={{ py: 5, textAlign: "center" }}>
          <CircularProgress aria-label="Carregando regra de cobertura" />
        </Box>
      ) : !saved ? (
        <Button variant="outlined" onClick={() => setRevision((value) => value + 1)}>
          Tentar carregar novamente
        </Button>
      ) : (
        <Box component="form" onSubmit={save}>
          <Box
            sx={{
              p: 2,
              mb: 3,
              bgcolor: "#f2f8f4",
              borderLeft: "3px solid #147d48",
              borderRadius: 1,
            }}
          >
            <Typography variant="caption" fontWeight={700}>
              REGRA ATUAL EM USO
            </Typography>
            <Typography variant="body2" mt={0.5}>
              Contar pixels com K ≥ <b>{display(saved.minKPercent)}%</b>. Classificar como colorido
              quando a cobertura total for ≥ <b>{display(saved.colorCoveragePercent)}%</b>.
            </Typography>
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
            {[
              {
                key: "minKPercent",
                title: "1. Pixels contabilizados",
                label: "K mínimo (%)",
                text: "Quantidade mínima de tinta preta em cada pixel não textual. Reduzir esse valor inclui áreas mais claras na contagem.",
              },
              {
                key: "colorCoveragePercent",
                title: "2. Classificação do livro",
                label: "Cobertura para colorido (%)",
                text: "Cobertura total mínima para informar que o livro será cobrado como colorido. O valor exato do limite também é incluído.",
              },
            ].map((field) => (
              <Box key={field.key}>
                <Typography variant="h6" mb={1}>
                  {field.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  {field.text}
                </Typography>
                <TextField
                  fullWidth
                  required
                  type="number"
                  label={field.label}
                  value={form[field.key]}
                  disabled={busy}
                  onChange={(event) => {
                    setForm((current) => ({ ...current, [field.key]: event.target.value }));
                    setSuccess("");
                  }}
                  inputProps={{ min: 0, max: 100, step: 0.01, inputMode: "decimal" }}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                  error={invalidField(field.key)}
                  helperText={
                    invalidField(field.key)
                      ? "Informe um valor de 0 a 100, com até duas casas decimais."
                      : "De 0 a 100, com até duas casas decimais."
                  }
                  sx={{ "& .MuiInputBase-root": { minHeight: 52 } }}
                />
              </Box>
            ))}
          </Box>
          {parsed && (parsed.minKPercent === 0 || parsed.colorCoveragePercent === 0) && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              K mínimo em 0% inclui pixels sem tinta preta. Cobertura mínima em 0% classifica todos
              os livros como coloridos.
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" mt={3}>
            A alteração vale para novas análises. Resultados já calculados e análises em andamento
            mantêm a regra usada no início. Para recalcular um livro, inicie uma nova análise do
            PDF.
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
              justifyContent: "space-between",
              alignItems: "center",
              borderTop: "1px solid #e4ebe7",
              mt: 3,
              pt: 2,
              "& .MuiButton-root": { minHeight: 44, width: { xs: "100%", sm: "auto" } },
            }}
          >
            <Button
              variant="outlined"
              disabled={busy}
              onClick={() => {
                setForm(fields(DEFAULT_COVERAGE_SETTINGS));
                setSuccess("");
              }}
            >
              Restaurar padrões (20% / 15%)
            </Button>
            <Button type="submit" variant="contained" disabled={busy || !dirty || !parsed}>
              {busy ? "Salvando…" : "Salvar regra de cobertura"}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
